import { addDays, EVENT_SCOPES, MORE_SCOPES, type EventScope } from './labels';

// ---------- typed days (owner remark 9: a typed date counts without picking it in the calendar) ----------

const pad = (n: number) => String(n).padStart(2, '0');
const realDay = (y: number, m: number, d: number): string | null => {
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d ? `${y}-${pad(m)}-${pad(d)}` : null;
};

/**
 * A day typed by a person -> 'YYYY-MM-DD', or null. Accepts "24.10.2026", "24.10.26", "24/10/2026", "24-10-2026",
 * "2026-10-24" and "24.10" (the year of `today`).
 */
export function parseDayText(text: string, today: string): string | null {
  const t = text.trim();
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) return realDay(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})[./-](\d{1,2})(?:[./-](\d{2}|\d{4}))?\.?$/.exec(t);
  if (!m) return null;
  const year = m[3] === undefined ? Number(today.slice(0, 4)) : m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  return realDay(year, Number(m[2]), Number(m[1]));
}

/** 'YYYY-MM-DD' -> "DD.MM.RRRR" ('' stays ''). */
export function formatDayText(day: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const [y, m, d] = day.split('-');
  return `${d}.${m}.${y}`;
}

/**
 * The last day after the first one changed: an empty last day, one that was the same as the old first day,
 * or one now before the first day becomes the first day; a later last day set by the person stays.
 */
export function lastDayAfterFirstChange({ oldFirst, newFirst, last }: { oldFirst: string; newFirst: string; last: string }): string {
  if (newFirst === '') return last;
  if (last === '' || last === oldFirst || last < newFirst) return newFirst;
  return last;
}

/**
 * One day with an end hour before the start hour means the event goes past midnight (a wedding till 02:00):
 * the last day to set, or null when nothing should move.
 */
export function endPastMidnight(d: { firstDay: string; lastDay: string; firstTime: string; lastTime: string }): string | null {
  if (d.firstDay === '' || d.lastDay !== d.firstDay || !d.firstTime || !d.lastTime) return null;
  return d.lastTime < d.firstTime ? addDays(d.firstDay, 1) : null;
}

// ---------- scopes of work (owner remarks 4, 5, 7) ----------

export type CustomScope = { id: string; label: string; deleted: boolean };
export type ScopeOption = { code: string; label: string };

/**
 * The picker's pills: the everyday fixed scopes and the custom ones on top, light / stage / video under "Więcej".
 * A deleted custom scope and the old "Inne" are shown only when the event already has them.
 */
export function scopeGroups(custom: readonly CustomScope[], selected: readonly string[]): { top: ScopeOption[]; more: ScopeOption[] } {
  const fixed = Object.keys(EVENT_SCOPES) as EventScope[];
  return {
    top: [
      ...fixed.filter((s) => !MORE_SCOPES.includes(s)).map((s) => ({ code: s, label: EVENT_SCOPES[s] })),
      ...custom.filter((c) => !c.deleted || selected.includes(c.id)).map((c) => ({ code: c.id, label: c.label })),
    ],
    more: MORE_SCOPES.filter((s) => s !== 'inne' || selected.includes(s)).map((s) => ({ code: s, label: EVENT_SCOPES[s] })),
  };
}

/** Names of the event's scopes (fixed and custom); an unknown id reads "Inny zakres". */
export function scopeNames(scopes: readonly string[], custom: readonly CustomScope[]): string[] {
  const names = new Map(custom.map((c) => [c.id, c.label]));
  return scopes.map((s) => (s in EVENT_SCOPES ? EVENT_SCOPES[s as EventScope] : (names.get(s) ?? 'Inny zakres')));
}
